import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Heading,
  Stack,
  Text,
  Flex,
  Divider,
  Image,
} from "@chakra-ui/react";
import { motion } from "framer-motion";

import visionIcon from "../../img/Vision@2x.png";
import missionIcon from "../../img/Mission1@2x.png";
import valuesIcon from "../../img/Value@2x.png";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionDivider = motion(Divider);

export default function VisionMission() {
  const mainColor = "#ffffff";
  const accentColor = "#c6a664";
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const vision = t("vision", { returnObjects: true });
  const mission = t("mission", { returnObjects: true });
  const values = t("values", { returnObjects: true });

  const sections = [
    { title: vision.title, description: vision.description, icon: visionIcon },
    { title: mission.title, description: mission.description, icon: missionIcon },
    { title: values.title, description: values.description, icon: valuesIcon },
  ];

  return (
    <Box
      id="vision-mission"
      py={{ base: 16, md: 24 }}
      dir={isArabic ? "rtl" : "ltr"}
      overflow="hidden"
      position="relative"
      bg="#000"
      color={mainColor}
      mt={5}
      key={i18n.language} 
    >
      {/* Subtle background gradient */}
      <Box
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        bgGradient="radial(at top, rgba(198,166,100,0.17), rgba(0,0,0,1))"
        zIndex={0}
      />

      <Container maxW="7xl" position="relative" zIndex={2}>
        {/* Section Heading */}
        <MotionHeading
          textAlign="center"
          fontSize={{ base: "2xl", md: "4xl" }}
          color={accentColor}
          mb={14}
          fontWeight="bold"
          letterSpacing="wide"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          {isArabic ? (
            <>
              الرؤية <Text as="span" color={mainColor}>، </Text> الرسالة{" "}
              <Text as="span" color={mainColor}>، </Text> القيم
            </>
          ) : (
            <>
              Vision <Text as="span" color={mainColor}>, </Text> Mission{" "}
              <Text as="span" color={mainColor}>, </Text> Values
            </>
          )}
        </MotionHeading>

        {/* Vision / Mission / Values */}
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={{ base: 12, md: 12 }}
          align="flex-start"
          justify="center"
          flexWrap="wrap"
        >
          {sections.map((item, index) => (
            <Flex key={`${item.title}-${i18n.language}`} flex="1" align="flex-start" justify="center" p={4}>
              <MotionBox
                textAlign={isArabic ? "right" : "left"}
                initial={{ opacity: 0, x: isArabic ? 60 : -60 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                viewport={{ once: true }}
                maxW="420px"
              >
                {/* Title with Icon */}
<Flex
  align="center"
  gap={3}
  mb={4}
  justify={isArabic ? "flex-end" : "flex-start"}
  direction={isArabic ? "row-reverse" : "row"} 
  textAlign={isArabic ? "right" : "left"}
>
  <Image
    src={item.icon}
    alt={`${item.title} icon`}
    boxSize={{ base: "35px", md: "45px" }}
    objectFit="contain"
    order={isArabic ? 2 : 1} 
  />

  <Heading
    fontSize="2xl"
    color={accentColor}
    textTransform="uppercase"
    letterSpacing="wide"
    order={isArabic ? 1 : 2}
  >
    {item.title}
  </Heading>
</Flex>


                <Divider borderColor={accentColor} borderWidth="2px" w="60px" mb={4} />

                <Text
                  fontSize="lg"
                  color="gray.200"
                  lineHeight="1.9"
                  _hover={{ color: accentColor, transition: "0.3s ease" }}
                >
                  {item.description}
                </Text>
              </MotionBox>

              {/* Divider between cards */}
              {index < sections.length - 1 && (
                <MotionDivider
                  orientation="vertical"
                  display={{ base: "none", md: "block" }}
                  borderColor="rgba(198,166,100,0.6)"
                  height="200px"
                  ml={6}
                  whileInView={{ opacity: 1, scaleY: 1 }}
                  initial={{ opacity: 0, scaleY: 0 }}
                  transition={{ duration: 0.8 }}
                />
              )}
            </Flex>
          ))}
        </Stack>
      </Container>

      {/* Bottom Glow Line */}
      <Box
        position="absolute"
        bottom="0"
        left="50%"
        transform="translateX(-50%)"
        w="60%"
        h="2px"
        bgGradient={`linear(to-r, transparent, ${accentColor}, transparent)`}
        opacity={0.5}
      />
    </Box>
  );
}
